/**
 * 免试镜直签系统（2026-10-25 新手引导批，docs/新手引导-设计方案.md §二——唯一 canonical）
 *
 * 一条通道、两种叙事包装：低咖位＝经纪人帮谈（broker，经纪人磨下来的小机会）、
 * 高咖位＝片方递本（pitch，片方指名邀约）。功能完全相同（免试镜直接签约的角色），
 * 差异只在派发条件（月掷率/番位档）与经纪人消息文案。
 *
 * 【供给铁律】从当季市场货架（player.marketScripts，scheduled 模式 A 位剧在前）挑剧，
 * **永不凭空造剧**——免试镜的只是「获取方式」不是「更好的池子」，150 本排期平衡零冲击。
 * 番位优先（帮谈=三四番、递本=一二番，不用竞角抢）；咖位尺按路径分段（2026-10-27 茄子令，
 * 详见 pickCandidate 注释）：帮谈=同档~高一级（经纪人磨够一够的机会）、递本 Lv.5~6=≤本档、
 * 递本 Lv.7+=低一/两档（片方冲咖位递本，同档走竞角）；性别合适、未被占用、未放弃过（同剧同角色不重递）。
 * 帮谈档内无三四番时五番兜底（小角色语境）；递本一二番无货当月不发（特权不降档给配角）。
 *
 * 【职责切分】本系统只管「派发决策」：何时掷（rollMonthly 月初/issueFirstOffer 建角后）、
 * 掷中挑哪部剧哪个角色（pickCandidate）、通知怎么投。**合同状态变更**（生成 pending/签约/
 * 放弃/市场换新修剪）一律走 ScriptSystem 的直签生命周期方法，⛔ 这里不自己摸 contracts 数组。
 *
 * 【呈现】（2026-10-30 签约呈现改造批，推翻 2026-10-25 的「聊天页消息 + 人脉红点」）＝**决策页入场送达链**：
 * 掷中只落 pending offer（合同本身），送达链由 app.ts 的 `deliverPendingOnEntry()` 在决策页入场时消费——
 * 经纪人 VN 一段（本系统 `speechOf` 出词）→ 重档签约弹窗（ContractSignPop）→ 两键强制二选一。
 * ⛔ 再入 `player.managerNotices`（直签消息从人脉页经纪人聊天里整体退场）；未处理的 offer 跟着
 * 送达链下一次入场重投，过期由 `rollMonthly` 统一作废（`signedMonth` 起两个月）。
 */
import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { Contract } from '../data/types';
import { EventBus, GameEvents } from '../core/EventBus';
import { ScriptSystem } from './ScriptSystem';

/** 派发叙事包装（一条通道两种说法，见类头注释） */
export type DirectSignKind = 'broker' | 'pitch';

/** 经纪人消息模板（2026-10-27 茄子定人设重写：三四十岁精干职业女性——直接给信息+一句务实判断，
 *  决定权交玩家；⛔ 寒暄铺垫（我跟你说）与吹嘘推销（随便挑不成问题）一类废话，口径同夏夏禁销售话术令） */
const BROKER_TALK = (scriptName: string, roleLabel: string) =>
    `《${scriptName}》的${roleLabel}，我帮你谈下来了。角色不大，剧本你看看喜不喜欢。`;
const PITCH_TALK = (scriptName: string, roleLabel: string) =>
    `《${scriptName}》剧组递了本子到我这，指名要你演${roleLabel}。条件我核过，没问题。`;

export class DirectSignSystem {
    private static instance: DirectSignSystem;

    private constructor() { }

    static getInstance(): DirectSignSystem {
        if (!this.instance) this.instance = new DirectSignSystem();
        return this.instance;
    }

    /** 当前挂着的待签直签 offer（决策页入场送达链的唯一读取口；无则 null） */
    pendingOffer(): { contract: Contract; kind: DirectSignKind } | null {
        const contract = GameState.getInstance().player?.contracts.find(c => c.status === 'pending');
        if (!contract) return null;
        return { contract, kind: this.kindOf(contract) };
    }

    /** 待签 offer 的经纪人台词（送达链 VN 段用；合同/角色缺失返回空串，调用方自行兜底） */
    speechOf(contract: Contract): string {
        const script = GameData.SCRIPT_MAP[contract.scriptId];
        const role = script?.roles[contract.roleIndex];
        if (!script || !role) return '';
        const label = role.name ? `${role.roleType}「${role.name}」` : role.roleType;
        return this.kindOf(contract) === 'broker'
            ? BROKER_TALK(script.name, label)
            : PITCH_TALK(script.name, label);
    }

    /**
     * 包装类型：合同上的 `directKind` 是唯一真相（`dispatch` 落合同时写上，跟着存档走）。
     * 老档没这个键时按番位回推一次（一二番=片方递本、其余=经纪人帮谈），下一次写盘就带字段了。
     * 回推会改口的是「直指剧本、跳过挑角」的单：月掷单本来就按番位分窗口（见 `pickCandidate`），
     * 猜必中；首单一律帮谈包装，却按开局咖位给到二番（Lv.3~4）和一番（Lv.5），那两档会被回推成递本。
     * 两种都只改一句台词的口吻，没有状态后果。
     */
    private kindOf(contract: Contract): DirectSignKind {
        if (contract.directKind) return contract.directKind;
        const role = GameData.SCRIPT_MAP[contract.scriptId]?.roles[contract.roleIndex];
        return (role?.billing ?? 9) <= 2 ? 'pitch' : 'broker';
    }

    /** 当前咖位的月掷率（供桩测读档位；Lv.1 起参与月掷 12%，开局首单另走 issueFirstOffer 不占掷点） */
    chanceFor(rank: number): number {
        if (rank < 1) return 0;
        return rank >= 5
            ? (GameConfig.DIRECT_SIGN_PITCH_LADDER[rank - 5] ?? 0)
            : GameConfig.DIRECT_SIGN_BROKER_CHANCE;
    }

    /**
     * 开局首单（必发）：建角 confirm 后由 CharacterCreatePanel 调用（intro VN 播完接上）。
     * 2026-10-27 滚动排期批改供给：首单=新手专属剧（GameConfig.NEWBIE_SCRIPT_ID，不占排期表），
     * 该剧不在排期表 ⇒ 开拍月回退「签约次月」，新手签约只等 1 个月进组。帮谈包装不变。
     * confirm 每周目恰好走一次，天然幂等无需 flag。
     * 2026-10-29 重写新手剧后改三档匹配（茄子令）：剧内三个女角色全部免试镜开放，
     * 按开局咖位发 1~2→女配角 / 3~4→女二号 / 5→女主角；三档 level 全部落 [rank, rank+1]
     * 帮谈窗口（开局 1 拿 Lv.2=新人第一个上台阶的机会，尺与月掷帮谈同一把）。
     * 玩家没签的角色（没发到的+发了放弃的）照常 sweepUnclaimedRoles 派 NPC。
     */
    issueFirstOffer(): boolean {
        const newbieId = GameConfig.NEWBIE_SCRIPT_ID;
        const newbie = GameData.SCRIPT_MAP[newbieId];
        const rank = GameState.getInstance().player.rank;
        const femaleOk = (r: { gender: string }) => r.gender === 'female' || r.gender === 'any';
        // 档位缺失递降兜底：女主→女二→女配（女配→女二→女主），不让首单断供
        const wantedChain = rank <= 2
            ? ['女配角', '女二号', '女主角']
            : rank <= 4 ? ['女二号', '女配角', '女主角'] : ['女主角', '女二号', '女配角'];
        let roleIndex = -1;
        for (const wanted of wantedChain) {
            roleIndex = newbie?.roles.findIndex(r => femaleOk(r) && r.roleType === wanted) ?? -1;
            if (roleIndex >= 0) break;
        }
        if (!newbie || roleIndex < 0) return this.dispatch('broker');   // 表缺失兜底走正常供给
        // 放弃语义（2026-10-25 拍板「同剧同角色不重复递」）：首单放弃后不再发第二张
        if (GameState.getInstance().player.flags[ScriptSystem.directDeclineKey(newbieId, roleIndex)]) return false;
        return this.dispatch('broker', { scriptId: newbieId, roleIndex });
    }

    /**
     * 晋升二线送本（必发一次，2026-10-27 茄子令「升到 Lv.5 固定送一个角色，专门写个剧本」）：
     * 首次跨入 Lv.5（GameLoop 晋升检查处调用）直签递晋升专属剧（PROMOTION_SCRIPT_ID）的一二番女角，
     * 递本包装（片方冲着新咖位来）。flag 防重=掉级再升不重发；剧不在排期表 ⇒ 开拍=签约次月，
     * offer 生命周期与常规单同（当月+次月有效，月初统一清）。放 deduction flag 先于 dispatch：掷点态可重入。
     */
    issuePromotionOffer(): boolean {
        const state = GameState.getInstance();
        if (state.player.flags['DIRECT_SIGN_PROMOTION_SENT']) return false;
        const scriptId = GameConfig.PROMOTION_SCRIPT_ID;
        const script = GameData.SCRIPT_MAP[scriptId];
        const roleIndex = script?.roles.findIndex(r =>
            (r.gender === 'female' || r.gender === 'any') && (r.billing ?? 9) <= 2) ?? -1;
        if (!script || roleIndex < 0) return false;   // 表缺失/无一二番合适性别角：不发也不锁 flag（表修好后的晋升窗口再判）
        state.setFlag('DIRECT_SIGN_PROMOTION_SENT');
        return this.dispatch('pitch', { scriptId, roleIndex });
    }

    /**
     * 月初掷点（GameLoop 月结算尾、NPC 招呼之后调）。
     * **第一步＝过期作废**（无条件，先于一切早退）：pending offer 从 `signedMonth` 起共两个月有效
     * （发出当月＋次月；月初清 `signedMonth < abs-1`，到点没签就 `voidDirectOffer`，不置放弃 flag——
     * 没赶上≠主动回绝）。⚠️ 用 `abs-1` 而不是 `abs`：晋升送本由 GameLoop 在 advanceMonth **之前**发出
     * （`issuePromotionOffer` 在月结序里），那座钟此刻还是旧月；按 `abs` 判会把当月刚发的晋升单一次拍死。
     * 之后掷点：Lv.1~4 帮谈 12%；Lv.5+ 递本阶梯；已有待签 offer 本月跳过（一次一单）。
     * 返回是否发出了新 offer。
     */
    rollMonthly(): boolean {
        const state = GameState.getInstance();
        if (!state.player) return false;
        const p = state.player;
        const abs = p.year * 12 + p.month;
        p.contracts
            .filter(c => c.status === 'pending' && c.signedMonth < abs - 1)
            .forEach(c => ScriptSystem.getInstance().voidDirectOffer(c.id));
        // 老档卫生：直签消息已从 managerNotices 整体退场（2026-10-30 签约呈现改造批），残留一次性清掉
        if (p.managerNotices.some(n => n.kind === 'directSign')) {
            p.managerNotices = p.managerNotices.filter(n => n.kind !== 'directSign');
            p.lastReadNoticeCount = Math.min(p.lastReadNoticeCount ?? 0, p.managerNotices.length);
        }

        const rank = p.rank;
        if (rank < 1) return false;
        if (p.contracts.some(c => c.status === 'pending')) return false;
        if (Math.random() >= this.chanceFor(rank)) return false;
        return this.dispatch(rank >= 5 ? 'pitch' : 'broker');
    }

    /**
     * 供给挑角：按市场顺序（scheduled 模式 A 位剧在前）逐剧找「番位档 ∧ 性别合 ∧ 咖位尺命中
     * ∧ 未占用 ∧ 未放弃过 ∧ 非专属锚」的角色，剧内先目标番位、帮谈再五番兜底；多候选随机挑一。
     * 全市场无货返回 null（当月不发——永不造剧铁律）。
     * 咖位尺分段（2026-10-27 茄子令，推翻旧「直签⛔越级」原则；三段都是两档宽窗口、随咖位逐段下移）：
     *  - 帮谈（Lv.1~4）：**level ∈ [rank, rank+1]**——同档与高一级（经纪人磨的就是够一够的机会）；
     *  - 递本 Lv.5~6：**level ∈ [rank-1, rank]**——低一档到同档；
     *  - 递本 Lv.7+：**level ∈ [rank-2, rank-1]**——片方冲着咖位递本，只递低一/两档的一二番（同档走竞角）。
     */
    private pickCandidate(kind: DirectSignKind): { scriptId: string; roleIndex: number } | null {
        const state = GameState.getInstance();
        const rank = state.player.rank;
        const passes: number[][] = kind === 'broker' ? [[3, 4], [5]] : [[1, 2]];
        for (const scriptId of state.player.marketScripts) {
            const script = GameData.SCRIPT_MAP[scriptId];
            if (!script) continue;
            // 选角已收口的剧⛔ 再递（揭晓清算/签约后 NPC 进组——货架 UI 已隐藏，这里同尺兜住）
            if (ScriptSystem.getInstance().isScriptClosedForAudition(scriptId)) continue;
            // 一剧一角色：在拍/已挂 offer 的剧不给新 offer
            if (state.player.contracts.some(c => c.scriptId === scriptId
                && (c.status === 'active' || c.status === 'pending'))) continue;
            for (const pass of passes) {
                const cands: number[] = [];
                script.roles.forEach((role, roleIndex) => {
                    if (!pass.includes(role.billing ?? -1)) return;
                    if (role.gender !== 'any' && role.gender !== state.player.gender) return;
                    if (kind === 'broker') {
                        if (role.level < rank || role.level > rank + 1) return;   // 帮谈窗口：同档~高一级
                    } else if (rank >= 7) {
                        if (role.level !== rank - 1 && role.level !== rank - 2) return;   // 递本高档：低一/两档
                    } else if (role.level < rank - 1 || role.level > rank) {
                        return;   // 递本 Lv.5~6：低一档~同档
                    }
                    if (GameData.roleAnchorOf(scriptId, role.name)?.mode === 'exclusive') return;
                    // 演过/在演/已完结的这个位不重复给（done/airing/active/pending 全档）
                    if (state.player.contracts.some(c => c.scriptId === scriptId && c.roleIndex === roleIndex)) return;
                    // 放弃过的同剧同角色不重递（放弃语义，flag 记忆）
                    if (state.player.flags[ScriptSystem.directDeclineKey(scriptId, roleIndex)]) return;
                    cands.push(roleIndex);
                });
                if (cands.length > 0) {
                    return { scriptId, roleIndex: cands[Math.floor(Math.random() * cands.length)] };
                }
            }
        }
        return null;
    }

    /**
     * 落合同（送达链的起点）+ 记下包装类型。fixed=首单/晋升单直指剧本跳过供给挑角。返回是否发出。
     * 2026-10-30 起不再投 managerNotices——送达链在决策页入场时消费 `pendingOffer()`。
     */
    private dispatch(kind: DirectSignKind, fixed?: { scriptId: string; roleIndex: number }): boolean {
        const state = GameState.getInstance();
        if (!state.player) return false;
        const pick = fixed ?? this.pickCandidate(kind);
        if (!pick) return false;
        const contract = ScriptSystem.getInstance().createDirectOffer(pick.scriptId, pick.roleIndex);
        if (!contract) return false;

        contract.directKind = kind;   // 包装跟着合同进档（读档回来口吻不变，见 kindOf）
        EventBus.emit(GameEvents.STATE_CHANGED);
        return true;
    }
}

export const directSignSystem = DirectSignSystem.getInstance();
